import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Image as ImageIcon } from 'lucide-react'
import OrdinalAudioCard from 'components/answers/FileEmbed/OrdinalAudioCard'
import { ordinalMediaTitle } from 'components/answers/FileEmbed/ordinalMediaTitle'
import VideoPlayerCore from 'components/answers/VideoPlayerCore'
import HTMLOrdinalLoader from 'components/answers/FileEmbed/HTMLOrdinalLoader'
import Ordinal3DLoader from 'components/answers/FileEmbed/Ordinal3DLoader'
import useStore from 'state/knovStore'
import {
    clearMediaTrackOrigin,
    registerMediaTrack,
    setMediaTrackOrigin,
} from 'state/mediaPlaybackCoordinator'
import type {
    OrdinalPreviewDispatchDecision,
    OrdinalPreviewReadyOptions,
} from 'components/shared/ordinalPreviewDispatch'
import { isOrdinalOutpoint, normalizeThreeDOrdiContentUrl } from 'components/shared/ordinalUrlUtils'
import OrdinalMarketControls from 'components/answers/FileEmbed/OrdinalMarketControls'
import { ordinalMediaKey } from 'components/answers/artifactMenuPolicy'
import type { IOrdinalMarketSummary } from 'api/api'

const ORDINAL_DATA_PATH_RE = /^\/api\/v1\/bsv\/ordinals\/[^\/]+\/data$/i
const ORDINAL_PREVIEW_IMAGE_PATH_RE = /^\/api\/v1\/bsv\/ordinals\/[^\/]+\/preview_image$/i

function cacheBust(url: string) {
    try {
        const u = new URL(url, window.location.href)
        u.searchParams.set('cb', String(Date.now()))
        return u.toString()
    } catch {
        return `${url}${url.includes('?') ? '&' : '?'}cb=${Date.now()}`
    }
}

function withPreviewSize(url: string | undefined, size: 'thumb' | 'grid' | 'list' | 'original') {
    if (!url) return undefined
    try {
        const u = new URL(url, window.location.href)
        // Only mutate our stable Rails preview endpoint; leave external URLs untouched.
        if (ORDINAL_PREVIEW_IMAGE_PATH_RE.test(u.pathname || '')) {
            u.searchParams.set('size', size)
            return u.toString()
        }
        return url
    } catch {
        return url
    }
}

const PREVIEW_SIZE_TO_PX: Record<string, number> = {
    thumb: 160,
    grid: 420,
    list: 960,
}

const stopMediaPropagation = (event: React.SyntheticEvent<HTMLElement>) => {
    event.stopPropagation()
}

/**
 * What names an inscription in the dock and on its card. Callers pass `title` as
 * an alt-text style label ("Ordinal by Ada"), which is a byline, not a title —
 * so the minting filename and then the origin txid both outrank it.
 */
function ordinalTrackTitle(
    kind: 'audio' | 'video',
    {
        filename,
        outpoint,
        bsvOrdinalId,
        title,
    }: { filename?: string; outpoint?: string; bsvOrdinalId?: string; title?: string },
) {
    const mediaTitle = ordinalMediaTitle({ filename, origin: outpoint, id: bsvOrdinalId })
    if (mediaTitle) return mediaTitle
    const trimmedTitle = title?.trim()
    if (trimmedTitle && trimmedTitle !== 'Ordinal Preview') return trimmedTitle
    return kind === 'audio' ? 'Ordinal audio' : 'Ordinal video'
}

interface OrdinalsRenderProps {
    isTreechat?: boolean
    outpoint?: string
    url?: string
    /** Optional BsvOrdinal id (allows client-side preview upload to canonical ordinal record) */
    bsvOrdinalId?: string
    className?: string
    allowInteraction?: boolean
    title?: string
    /**
     * Filename the inscription was minted from. Titles media the wrapper answer
     * leaves unnamed — see ordinalMediaTitle.
     */
    filename?: string
    /** Optional content-type hint to avoid HEAD */
    contentType?: string
    /** Use Babylon for glTF models */
    useBabylonForGltf?: boolean
    /**
     * For 3D models: auto-mount the viewer to capture/upload a preview image,
     * then auto-hide once a preview is available.
     */
    autoCapture3dPreview?: boolean
    /** Alignment mode for injected HTML docs */
    htmlAlign?: 'center' | 'top-center'
    /** Keep HTML cards on a stable screenshot surface until the user activates live content. */
    passiveHtmlPreview?: boolean
    /** Defer address-based HTML screenshot lookup until the caller is near the viewport. */
    htmlPreviewLookupEnabled?: boolean
    /** Existing preview image URL (if any) */
    previewUrl?: string
    /** When rendering an image from `previewUrl`, request this variant size (non-interactive only) */
    previewSize?: 'thumb' | 'grid' | 'list' | 'original'
    /** Optional Answer id (for socket link delivery in composer) */
    answerId?: string
    /** The post (and its thread) embedding this ordinal, for an HTML ordinal's wallet bridge. */
    walletPost?: { answerId: string; threadId?: string | null } | null
    /** Ordinal stream context used by the media dock's artifact controls. */
    artifactOrdinalId?: string
    artifactOwnerId?: string
    /** Callback to disable/enable Post button in composer */
    setPostDisabled?: (disabled: boolean) => void
    /** When true, ensure a Url record exists via UrlProcessorWorker before screenshot */
    ensureUrl?: boolean
    forceLive?: boolean
    onRequestLive?: () => void
    onPreviewPending?: () => void
    onPreviewReady?: (options?: OrdinalPreviewReadyOptions) => void
    /** When true, the preview was generated from a link preview */
    fromLinkPreview?: boolean
    previewDispatchDecision?: OrdinalPreviewDispatchDecision
    /** Fill fixed-size preview cards while keeping the video itself contained. */
    fillVideoContainer?: boolean
    /** Size video-only feed cards to the loaded video's native aspect ratio. */
    naturalVideo?: boolean
    /**
     * Image-only hero mode: let the artwork set its own height (natural aspect
     * up to a viewport-friendly cap) instead of letterboxing into a fixed-height
     * parent. Callers should only pass this for image content types.
     */
    naturalMedia?: boolean
    /**
     * Overlay the bid/ask market rail (list/cancel, offer/withdraw, price pill)
     * on the artwork. Market availability is resolved centrally and requires
     * an ordinal id (bsvOrdinalId or artifactOrdinalId) to resolve.
     */
    showBidAsk?: boolean
    /** Seed market state from a stream payload to avoid per-tile fetches. */
    marketSummary?: IOrdinalMarketSummary | null
    /**
     * Placeholder palette for audio ordinals without artwork: the ordinals
     * stream wears its bitcoin orange; audio surfacing inside thread streams
     * stays on the app purple.
     */
    audioPalette?: 'purple' | 'orange'
}

const OrdinalsRender: React.FC<OrdinalsRenderProps> = ({
    isTreechat: _isTreechat = false,
    outpoint,
    url,
    bsvOrdinalId,
    className,
    allowInteraction = true,
    title = 'Ordinal Preview',
    filename,
    contentType,
    useBabylonForGltf = true,
    autoCapture3dPreview = false,
    htmlAlign = 'top-center',
    passiveHtmlPreview = false,
    htmlPreviewLookupEnabled = true,
    previewUrl,
    previewSize = 'grid',
    answerId,
    walletPost,
    artifactOrdinalId,
    artifactOwnerId,
    setPostDisabled,
    ensureUrl = false,
    forceLive = false,
    onRequestLive,
    onPreviewPending,
    onPreviewReady,
    fromLinkPreview = false,
    previewDispatchDecision = 'dispatch',
    fillVideoContainer = false,
    naturalVideo = false,
    naturalMedia = false,
    showBidAsk = false,
    marketSummary,
    audioPalette = 'purple',
}) => {
    const debug = useStore(state => state.DEBUG)
    const rootRef = React.useRef<HTMLDivElement | null>(null)
    // Assume contentType is correct and authoritative (server-side). No byte sniffing.
    const hintedType = (contentType || '').toLowerCase()
    const type = hintedType
    const isHtml = type.includes('text/html')
    const isUnknownOrBinary =
        !type ||
        type.includes('octet-stream') ||
        type === 'application/binary' ||
        type === 'binary/octet-stream'

    const src = useMemo(() => {
        // Prefer the provided `url` (e.g. Treechat cached S3 redirect endpoint).
        // Only normalize viewer-style 3dordi URLs to the canonical content host.
        if (url) {
            const normalizedThreeDOrdiUrl = normalizeThreeDOrdiContentUrl(url)
            if (normalizedThreeDOrdiUrl) return normalizedThreeDOrdiUrl
            return url
        }

        // Fallback: derive from outpoint (inscription origin outpoint)
        if (isOrdinalOutpoint(outpoint))
            return `https://ordinals.gorillapool.io/content/${outpoint}`

        return ''
    }, [url, outpoint])
    const audioMediaKey = useMemo(
        () => ordinalMediaKey('audio', { bsvOrdinalId, outpoint, src }),
        [bsvOrdinalId, outpoint, src],
    )
    const videoMediaKey = useMemo(
        () => ordinalMediaKey('video', { bsvOrdinalId, outpoint, src }),
        [bsvOrdinalId, outpoint, src],
    )
    const audioTrackTitle = useMemo(
        () => ordinalTrackTitle('audio', { filename, outpoint, bsvOrdinalId, title }),
        [bsvOrdinalId, filename, outpoint, title],
    )
    const videoTrackTitle = useMemo(
        () => ordinalTrackTitle('video', { filename, outpoint, bsvOrdinalId, title }),
        [bsvOrdinalId, filename, outpoint, title],
    )

    // If glTF and Babylon requested, render Babylon directly
    const ct = hintedType
    const shouldUseBabylon =
        useBabylonForGltf &&
        (ct.includes('model/gltf-binary') ||
            ct.includes('model/gltf+json') ||
            ct.includes('model/gltf'))

    // HTML rendering:
    // In dev (or any split-origin setup), the Rails proxy endpoint includes X-Frame-Options:SAMEORIGIN,
    // which blocks iframing `knovApiUrl` from the Vite origin. For HTML specifically, we prefer the
    // canonical external host so the iframe is a true separate origin (and avoid frame blocking).
    const htmlSrc = useMemo(() => {
        if (!isHtml) return src
        if (!outpoint || !isOrdinalOutpoint(outpoint)) return src
        if (!src) return src
        try {
            // Support relative URLs, though we usually pass absolute.
            const u = new URL(src, window.location.href)
            const isOrdinalDataEndpoint = /^\/api\/v1\/bsv\/ordinals\/[^\/]+\/data$/.test(
                u.pathname || '',
            )
            const isCrossOrigin = u.origin !== window.location.origin
            if (isOrdinalDataEndpoint && isCrossOrigin)
                return `https://ordinals.gorillapool.io/content/${outpoint}`
        } catch (e) {}
        return src
    }, [isHtml, src, outpoint])

    const containerStyle: React.CSSProperties =
        naturalMedia || naturalVideo
            ? {
                  // Height comes from the media itself: natural images cap at a
                  // viewport-friendly height, while natural videos use their aspect ratio.
                  width: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
              }
            : {
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
              }
    const mediaStyle: React.CSSProperties = naturalMedia
        ? {
              maxWidth: '100%',
              maxHeight: 'min(520px, 62vh)',
              objectFit: 'contain',
          }
        : {
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
          }

    // Determine which content to render
    let content: React.ReactNode = null
    let wrapperStyle: React.CSSProperties | undefined = containerStyle

    // For images, prefer the canonical src but fall back to previewUrl if the fetch fails.
    // This avoids showing broken-image icons when the internal `/data` endpoint responds with JSON
    // (e.g. 202 "hydrating") before cached bytes are available.
    const sizedPreviewUrl = useMemo(() => {
        // In non-interactive contexts (grid/list previews), prefer a smaller variant to reduce bandwidth.
        // In interactive/lightbox contexts, callers should already request original if desired.
        if (!previewUrl) return undefined
        if (allowInteraction) return previewUrl
        return withPreviewSize(previewUrl, previewSize)
    }, [previewUrl, allowInteraction, previewSize])

    const initialImgSrc = useMemo(() => {
        // If we have a preview image, use it as the primary src in non-interactive contexts.
        if (!allowInteraction && sizedPreviewUrl) return sizedPreviewUrl
        return src
    }, [allowInteraction, sizedPreviewUrl, src])

    const [imgSrc, setImgSrc] = useState<string>(initialImgSrc)
    const [imgRetryCount, setImgRetryCount] = useState<number>(0)
    const [imgTriedPreview, setImgTriedPreview] = useState<boolean>(false)
    const [imgTriedSrcFallback, setImgTriedSrcFallback] = useState<boolean>(false)
    const [imgTriedPreviewRefresh, setImgTriedPreviewRefresh] = useState<boolean>(false)
    const [imgHardError, setImgHardError] = useState<boolean>(false)
    const [imgDims, setImgDims] = useState<{ w: number; h: number } | null>(null)
    const [textContent, setTextContent] = useState<string>('')
    const [textStatus, setTextStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle')
    const imgRetryTimerRef = React.useRef<number | null>(null)
    const knovApiOrigin = useMemo(() => {
        try {
            return new URL(window.knovApiUrl).origin
        } catch {
            return ''
        }
    }, [])
    useEffect(() => {
        setImgSrc(initialImgSrc)
        setImgRetryCount(0)
        // If we start on the preview URL, mark it as already tried.
        setImgTriedPreview(!allowInteraction && !!sizedPreviewUrl)
        setImgTriedSrcFallback(false)
        setImgTriedPreviewRefresh(false)
        setImgHardError(false)
        setImgDims(null)
    }, [src, initialImgSrc, allowInteraction, sizedPreviewUrl])

    useEffect(() => {
        // Only the text/plain render branch consumes textStatus; text/html renders through the iframe
        // (htmlSrc), so fetching /data for an html ordinal is wasted work whose (CORS) failure needlessly
        // flips textStatus and re-renders. Skip it for html.
        if (!type.startsWith('text/') || isHtml || !src) {
            setTextContent('')
            setTextStatus('idle')
            return
        }

        const controller = new AbortController()
        setTextContent('')
        setTextStatus('loading')

        // The ordinal /data endpoint is public (no auth cookie required). Sending credentials on a
        // cross-origin request (prod: app.treechat.com -> knov-prod content host) forces the server to
        // echo Access-Control-Allow-Credentials: true, which it does not — so the credentialed fetch is
        // CORS-blocked every time, the text status flips to 'error', and the card height churns. Only
        // attach credentials for same-origin requests; cross-origin GETs are already allowed by rack-cors.
        let sameOrigin = false
        try {
            sameOrigin = new URL(src, window.location.href).origin === window.location.origin
        } catch {}

        fetch(src, { signal: controller.signal, credentials: sameOrigin ? 'include' : 'omit' })
            .then(async response => {
                if (response.status === 202 || !response.ok) {
                    throw new Error(`Failed to load text ordinal (${response.status})`)
                }

                return response.text()
            })
            .then(body => {
                if (controller.signal.aborted) return
                setTextContent(body)
                setTextStatus('ready')
            })
            .catch(error => {
                if (controller.signal.aborted) return
                console.warn('Failed to load text ordinal:', error)
                setTextContent('')
                setTextStatus('error')
            })

        return () => controller.abort()
    }, [bsvOrdinalId, outpoint, src, type, isHtml])

    useEffect(() => {
        return () => {
            if (imgRetryTimerRef.current) window.clearTimeout(imgRetryTimerRef.current)
        }
    }, [])

    useLayoutEffect(() => {
        if (!type.startsWith('audio/') || !audioMediaKey || !src) return undefined

        registerMediaTrack(audioMediaKey, {
            kind: 'audio',
            src,
            title: audioTrackTitle,
            posterSrc: previewUrl || null,
            answerId: answerId || null,
            ...(artifactOrdinalId
                ? {
                      artifactOrdinalId,
                      artifactOwnerId: artifactOwnerId || null,
                  }
                : {}),
        })

        const originEl = rootRef.current
        setMediaTrackOrigin(audioMediaKey, originEl)
        return () => clearMediaTrackOrigin(audioMediaKey, originEl)
    }, [
        answerId,
        artifactOrdinalId,
        artifactOwnerId,
        audioMediaKey,
        audioTrackTitle,
        previewUrl,
        src,
        type,
    ])

    // If we initially load a preview variant URL but it still resolves to a huge image (variant not ready),
    // retry once with a cache-buster so we can pick up the processed variant shortly after it finishes.
    useEffect(() => {
        if (allowInteraction) return
        if (!sizedPreviewUrl) return
        if (imgSrc !== sizedPreviewUrl) return
        if (!imgDims) return
        if (imgTriedPreviewRefresh) return

        const px = PREVIEW_SIZE_TO_PX[String(previewSize)]
        if (!px) return
        const tooLarge = imgDims.w > px + 6 || imgDims.h > px + 6
        if (!tooLarge) return

        const t = window.setTimeout(() => {
            setImgTriedPreviewRefresh(true)
            setImgSrc(cacheBust(sizedPreviewUrl))
        }, 800)

        return () => window.clearTimeout(t)
    }, [allowInteraction, sizedPreviewUrl, imgSrc, imgDims, imgTriedPreviewRefresh, previewSize])

    if (shouldUseBabylon) {
        content = (
            <Ordinal3DLoader
                src={src}
                bsvOrdinalId={bsvOrdinalId}
                allowInteraction={allowInteraction}
                previewUrl={previewUrl}
                autoCapturePreview={autoCapture3dPreview}
                forceLive={forceLive}
                onRequestLive={onRequestLive}
                onPreviewPending={onPreviewPending}
                onPreviewReady={onPreviewReady}
                fromLinkPreview={fromLinkPreview}
                previewDispatchDecision={previewDispatchDecision}
            />
        )
    } else if (isHtml) {
        // HTMLOrdinalLoader handles its own styling, but the outer wrapper still needs a
        // deterministic 100% sizing context (some callsites don't pass a sizing className).
        const previewAddress = isOrdinalOutpoint(outpoint)
            ? `https://ordinals.gorillapool.io/content/${outpoint}`
            : htmlSrc
        content = (
            <HTMLOrdinalLoader
                src={htmlSrc}
                previewAddress={previewAddress}
                allowInteraction={allowInteraction}
                title={title}
                htmlAlign={htmlAlign}
                passivePreview={passiveHtmlPreview}
                previewLookupEnabled={htmlPreviewLookupEnabled}
                previewUrl={previewUrl}
                answerId={answerId}
                walletPost={walletPost}
                setPostDisabled={setPostDisabled}
                ensureUrl={ensureUrl}
                forceLive={forceLive}
                onRequestLive={onRequestLive}
                onPreviewPending={onPreviewPending}
                onPreviewReady={onPreviewReady}
                fromLinkPreview={fromLinkPreview}
                previewDispatchDecision={previewDispatchDecision}
            />
        )
    } else if (type.startsWith('image/')) {
        //console.log('ordinal render image', outpoint, type, src)
        if (imgHardError) {
            content = (
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        padding: 10,
                        color: 'rgba(255, 255, 255, 0.78)',
                        background: 'rgba(0, 0, 0, 0.10)',
                        textAlign: 'center',
                    }}
                >
                    <ImageIcon size={24} strokeWidth={2.2} />
                    <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.95 }}>
                        Image failed to load
                    </div>
                </div>
            )
        } else {
            content = (
                <>
                    <img
                        src={imgSrc}
                        alt={title}
                        style={mediaStyle}
                        loading="lazy"
                        decoding="async"
                        data-bsv-ordinals-id={bsvOrdinalId ? String(bsvOrdinalId) : undefined}
                        data-bsv-ordinals-origin={outpoint ? String(outpoint) : undefined}
                        onLoad={e => {
                            try {
                                const el = e.currentTarget
                                const w = el.naturalWidth
                                const h = el.naturalHeight
                                if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
                                    // Only store dimensions when needed:
                                    // - DEBUG overlay, or
                                    // - non-interactive preview variants where we might need to refresh a too-large image.
                                    const needsPreviewDims =
                                        !allowInteraction &&
                                        !!sizedPreviewUrl &&
                                        imgSrc === sizedPreviewUrl &&
                                        !imgTriedPreviewRefresh
                                    if (debug || needsPreviewDims) setImgDims({ w, h })
                                }
                            } catch {}
                        }}
                        onError={() => {
                            // If we started with a preview variant and it failed, fall back to the canonical src (/data).
                            // Only do this once to avoid loops.
                            if (
                                sizedPreviewUrl &&
                                imgSrc === sizedPreviewUrl &&
                                src &&
                                imgSrc !== src &&
                                !imgTriedSrcFallback
                            ) {
                                setImgTriedSrcFallback(true)
                                setImgSrc(src)
                                return
                            }

                            // If our API host drops connections under load (common during long scroll),
                            // retry a few times with exponential-ish backoff and a cache-buster.
                            try {
                                const u = new URL(imgSrc, window.location.href)
                                const isOrdinalDataEndpoint = ORDINAL_DATA_PATH_RE.test(
                                    u.pathname || '',
                                )
                                const isOrdinalPreviewEndpoint = ORDINAL_PREVIEW_IMAGE_PATH_RE.test(
                                    u.pathname || '',
                                )
                                const isKnovApi = !!knovApiOrigin && u.origin === knovApiOrigin
                                const isRetryable =
                                    isKnovApi && (isOrdinalDataEndpoint || isOrdinalPreviewEndpoint)
                                const delaysMs = [1200, 3500, 9000]
                                if (isRetryable && imgRetryCount < delaysMs.length) {
                                    const delay = delaysMs[imgRetryCount]
                                    setImgRetryCount(c => c + 1)
                                    if (imgRetryTimerRef.current) {
                                        window.clearTimeout(imgRetryTimerRef.current)
                                    }
                                    imgRetryTimerRef.current = window.setTimeout(() => {
                                        setImgSrc(cacheBust(imgSrc))
                                    }, delay)
                                    return
                                }
                            } catch {}

                            if (sizedPreviewUrl && imgSrc !== sizedPreviewUrl && !imgTriedPreview) {
                                setImgTriedPreview(true)
                                setImgSrc(sizedPreviewUrl)
                                return
                            }

                            setImgHardError(true)
                        }}
                    />
                    {debug && imgDims && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 6,
                                left: 6,
                                zIndex: 5,
                                padding: '3px 6px',
                                borderRadius: 6,
                                fontSize: 11,
                                lineHeight: 1.2,
                                background: 'rgba(0,0,0,0.60)',
                                color: 'rgba(255,255,255,0.92)',
                                pointerEvents: 'none',
                                fontFamily:
                                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                maxWidth: 'calc(100% - 12px)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                            title={`${imgDims.w}×${imgDims.h} (${
                                imgSrc === sizedPreviewUrl ? 'preview' : 'src'
                            })`}
                        >
                            {imgDims.w}×{imgDims.h}
                            {imgSrc === sizedPreviewUrl ? ' (preview)' : ' (src)'}
                        </div>
                    )}
                </>
            )
        }
    } else if (type.startsWith('video/')) {
        content = (
            <div
                style={{ width: '100%', height: naturalVideo ? 'auto' : '100%' }}
                data-no-modal-interaction
                onClick={stopMediaPropagation}
                onMouseDown={stopMediaPropagation}
                onPointerDown={stopMediaPropagation}
                onTouchStart={stopMediaPropagation}
            >
                <VideoPlayerCore
                    mediaKey={videoMediaKey}
                    src={src}
                    type={type || undefined}
                    trackTitle={videoTrackTitle}
                    trackAnswerId={answerId || undefined}
                    trackArtifactOrdinalId={artifactOrdinalId}
                    trackArtifactOwnerId={artifactOwnerId}
                    rootStyle={
                        naturalVideo
                            ? { height: 'auto' }
                            : fillVideoContainer
                            ? { height: '100%' }
                            : undefined
                    }
                    containerStyle={
                        naturalVideo
                            ? { height: 'auto', maxHeight: 'none' }
                            : fillVideoContainer
                            ? { height: '100%', maxHeight: 'none' }
                            : undefined
                    }
                    useNativeAspectRatio={naturalVideo}
                    posterSrc={previewUrl || null}
                    showFirstFramePreview={!previewUrl && !forceLive}
                    preload="metadata"
                    fallbackAspectRatio="16 / 9"
                    preloadVideoLink
                    preloadVideoOnHover
                    onLoadedMetadata={e => {
                        try {
                            const el = e.currentTarget
                            // If we're at t=0, nudge forward a hair so iOS paints the first frame.
                            if (
                                Number.isFinite(el.duration) &&
                                el.duration > 0 &&
                                el.currentTime < 0.001
                            ) {
                                el.currentTime = 0.01
                            }
                        } catch {}
                    }}
                />
            </div>
        )
    } else if (type.startsWith('audio/')) {
        content = (
            <OrdinalAudioCard
                key={`${src}:${type}`}
                src={src}
                mediaKey={audioMediaKey}
                title={audioTrackTitle}
                previewUrl={previewUrl}
                answerId={answerId}
                palette={audioPalette}
                canDownload={
                    Boolean(artifactOwnerId) &&
                    String(artifactOwnerId) === String(window.gon?.currentUser?.id)
                }
            />
        )
    } else if (type === 'application/pdf') {
        content = (
            <embed src={src} type="application/pdf" style={{ width: '100%', height: '100%' }} />
        )
    } else if (type.startsWith('text/')) {
        if (textStatus === 'ready') {
            content = (
                <pre
                    style={{
                        width: '100%',
                        height: '100%',
                        margin: 0,
                        padding: 12,
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: 13,
                        lineHeight: 1.5,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                >
                    {textContent}
                </pre>
            )
        } else if (textStatus === 'error') {
            content = (
                <div style={{ padding: 12, textAlign: 'center', opacity: 0.8 }}>
                    Unable to load text content.
                </div>
            )
        } else {
            content = (
                <div style={{ padding: 12, textAlign: 'center', opacity: 0.8 }}>
                    Loading text content...
                </div>
            )
        }
    } else if (isUnknownOrBinary) {
        // Safety: embedding unknown binary responses can trigger browser downloads.
        // Prefer a user-initiated navigation instead of auto-loading bytes.
        content = (
            <div style={{ padding: 10, textAlign: 'center' }}>
                {previewUrl ? (
                    <img
                        src={previewUrl}
                        alt={title}
                        style={mediaStyle}
                        loading="lazy"
                        decoding="async"
                    />
                ) : (
                    <div style={{ opacity: 0.8, marginBottom: 6 }}>Unknown file type</div>
                )}
                {src ? (
                    <a
                        href={src}
                        target="_blank"
                        rel="noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ textDecoration: 'underline' }}
                    >
                        Open
                    </a>
                ) : null}
            </div>
        )
    } else {
        content = <object data={src} style={{ width: '100%', height: '100%' }} />
    }

    const marketOrdinalId = bsvOrdinalId || artifactOrdinalId

    return (
        <div
            ref={rootRef}
            className={className}
            style={showBidAsk ? { position: 'relative', ...wrapperStyle } : wrapperStyle}
            data-bsv-ordinals-id={bsvOrdinalId ? String(bsvOrdinalId) : undefined}
            data-bsv-ordinals-origin={outpoint ? String(outpoint) : undefined}
        >
            {content}
            {showBidAsk && marketOrdinalId && (
                <OrdinalMarketControls
                    ordinalId={String(marketOrdinalId)}
                    ownerId={artifactOwnerId}
                    origin={outpoint}
                    initialMarket={marketSummary}
                />
            )}
        </div>
    )
}

export default React.memo(
    OrdinalsRender,
    (prev, next) =>
        prev.outpoint === next.outpoint &&
        prev.url === next.url &&
        prev.bsvOrdinalId === next.bsvOrdinalId &&
        prev.className === next.className &&
        prev.allowInteraction === next.allowInteraction &&
        prev.title === next.title &&
        prev.filename === next.filename &&
        prev.contentType === next.contentType &&
        prev.useBabylonForGltf === next.useBabylonForGltf &&
        prev.autoCapture3dPreview === next.autoCapture3dPreview &&
        prev.htmlAlign === next.htmlAlign &&
        prev.passiveHtmlPreview === next.passiveHtmlPreview &&
        prev.htmlPreviewLookupEnabled === next.htmlPreviewLookupEnabled &&
        prev.previewUrl === next.previewUrl &&
        prev.previewSize === next.previewSize &&
        prev.answerId === next.answerId &&
        prev.walletPost?.answerId === next.walletPost?.answerId &&
        prev.walletPost?.threadId === next.walletPost?.threadId &&
        prev.artifactOrdinalId === next.artifactOrdinalId &&
        prev.artifactOwnerId === next.artifactOwnerId &&
        prev.setPostDisabled === next.setPostDisabled &&
        prev.ensureUrl === next.ensureUrl &&
        prev.forceLive === next.forceLive &&
        prev.onRequestLive === next.onRequestLive &&
        prev.onPreviewPending === next.onPreviewPending &&
        prev.onPreviewReady === next.onPreviewReady &&
        prev.fromLinkPreview === next.fromLinkPreview &&
        prev.previewDispatchDecision === next.previewDispatchDecision &&
        prev.fillVideoContainer === next.fillVideoContainer &&
        prev.naturalVideo === next.naturalVideo &&
        prev.naturalMedia === next.naturalMedia &&
        prev.showBidAsk === next.showBidAsk &&
        prev.marketSummary === next.marketSummary &&
        prev.audioPalette === next.audioPalette,
)
